import { KeyEvent } from 'library/ts/ui/events/KeyEvent';
import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';
import * as Type from 'library/ts/utilities/modules/Type';

/* eslint-disable */

/**
 * @description Hotkeys options interface
 */
interface IHotkeysOptions {
	autoEnable: boolean;
	debounceTime: number;
}

interface IHotkey {
	[ key: string ]: boolean;
}

interface IHotkeysListener {
	callback: Function;
	hotkey: IHotkey[];
}

export interface IHotkeyContext {
	register: ( hotkey: string, callback: Function ) => void;
	unregister: ( hotkey: string, callback: Function ) => void;
	enable: () => void;
	disable: () => void;
}

const isObjectEqual = (
	objectA: { [ key: string ]: boolean },
	objectB: { [ key: string ]: boolean }
): boolean => {
	const aKeys = Object.keys( objectA );

	if ( aKeys.length !== Object.keys( objectB ).length ) {
		return false;
	}

	return aKeys.every(
		( key ) => ( Object.prototype.hasOwnProperty.call( objectB, key )
			&& objectA[ key ] === objectB[ key ] )
	);
};

const isArrayEqual = ( a, b ) => a.length === b.length
	&& a.every( ( v, i ) => isObjectEqual( v, b[ i ] ) );

export const matchHotkey = ( buffer: IHotkey[], hotkey: IHotkey[] ): boolean => {
	if ( buffer.length < hotkey.length ) {
		return false;
	}

	const indexDiff = buffer.length - hotkey.length;
	for ( let i = hotkey.length - 1; i >= 0; i -= 1 ) {
		if ( !isObjectEqual( buffer[ indexDiff + i ], hotkey[ i ] ) ) {
			return false;
		}
	}

	return true;
};

/**
 * @description Convert array of strings to object
 * @param {string[]} array
 * @return {{[p: string]: boolean}}
 */
const arrayToObject = ( array: string[] ): { [ key: string ]: boolean } => (
	array.reduce(
		( obj, key ) => ( { ...obj, [ key ]: true } ),
		{},
	)
);

const allModifiers: string[] = [ 'ctrl', 'shift', 'alt', 'meta' ];
const indexedModifiers: { [ key: string ]: boolean } = arrayToObject( allModifiers );

const isHotkeyValid = ( hotkey: IHotkey ) =>
	Object.keys( hotkey ).filter( ( key ) => !indexedModifiers[ key ] ).length === 1;

/**
 * @description Validates the value and throws the Error if value is false
 * @param {boolean} value
 * @param {string} message
 */
const validate = ( value: boolean, message: string ) => {
	if ( !value ) {
		throw new Error( message );
	}
};

/**
 * @description Validates data type
 * @param {any} value
 * @param {string} name
 * @param {string} type
 */
const validateType = ( value: any, name: string, type: string ) => {
	validate(
		typeof value === type,
		`The ${name} must be a ${type}; given ${typeof value}`,
	);
};

/**
 * @description Normalize hotkey into array of objects
 * @param {string} hotkey
 * @return {IHotkey[]}
 */
export const normalizeHotkey = ( hotkey: string ): IHotkey[] => (
	hotkey.split( / +/g ).map(
		( part ) => {
			const array = part.split( '+' ).filter( Boolean );
			const result = arrayToObject( array );

			validate(
				Object.keys( result ).length >= array.length,
				`Hotkey combination has duplicates "${hotkey}"`,
			);

			validate(
				isHotkeyValid( result ),
				`Invalid hotkey combination: "${hotkey}"`,
			);

			return result;
		},
	)
)

const validateListenerArguments = ( hotkey: string, callback: Function ) => {
	validateType( hotkey, 'hotkey', TrueType.STRING );
	validateType( callback, 'callback', TrueType.FUNCTION );
};

const createListenersFunction = ( listeners: IHotkeysListener[], listenerFunction: Function ) => (
	( hotkey: string, callback: Function ) => {
		validateListenerArguments( hotkey, callback );
		listenerFunction( listeners, hotkey, callback );
	}
)

const registerListener = ( listeners: IHotkeysListener[], hotkey: string, callback: Function ) => {
	listeners.push( { hotkey: normalizeHotkey( hotkey ), callback } );
};

const unregisterListener = ( listeners: IHotkeysListener[], hotkey: string, callback: Function ) => {
	const normalized: IHotkey[] = normalizeHotkey( hotkey );

	const index = listeners.findIndex(
		( listener ) => listener.callback === callback
			&& isArrayEqual( normalized, listener.hotkey ),
	);

	if ( index !== -1 ) {
		listeners.splice( index, 1 );
	}
};

const debounce = ( debounceFunction: Function, debounceTime: number ): Function => {
	let timeoutId = null;

	return () => {
		clearTimeout( timeoutId );
		timeoutId = setTimeout( debounceFunction, debounceTime );
	};
};

const getKey = ( key: string ): string => {
	switch ( key ) {
		case '+':
			return 'plus';
		case ' ':
			return 'space';
		default:
			return key;
	}
};

const createKeyDownListener = ( listeners: IHotkeysListener[], debounceTime: number ) => {
	let buffer: IHotkey[] = [];

	const clearBufferDebounced = debounce(
		() => {
			buffer = [];
		},
		debounceTime,
	);

	return ( event ) => {
		if ( event.repeat ) {
			return;
		}

		if ( Type.isFunction( event?.getModifierState ) && event.getModifierState( event.key ) ) {
			return;
		}

		clearBufferDebounced();

		const description = {
			[ getKey( event.key ) ]: true,
		};

		allModifiers.forEach(
			( modifier ) => {
				if ( event[ `${modifier}Key` ] ) {
					description[ modifier ] = true;
				}
			}
		);

		buffer.push( description );

		listeners.forEach(
			( listener ) => {
				if ( matchHotkey( buffer, listener.hotkey ) ) {
					listener.callback( event );
				}
			}
		);
	};
};

/**
 * @description Validate hotkeys options
 * @param {IHotkeysOptions} options
 * @return {IHotkeysOptions}
 */
const validateContext = ( options: IHotkeysOptions ): IHotkeysOptions => {
	validateType( options.debounceTime, 'debounceTime', TrueType.NUMBER );
	validate( options.debounceTime > 0, 'debounceTime must be > 0' );
	validateType( options.autoEnable, 'autoEnable', TrueType.BOOLEAN );
	return options;
};

/**
 * @see {@link https://medium.com/better-programming/full-featured-hotkeys-library-in-200-lines-of-javascript-code-81a74e3138cc}
 * @see {@link https://gist.github.com/abuduba/c41d40a1b45990c086de6d3b24bdd85a}
 * @param {IHotkeysOptions} options
 * @return {{enable: () => void, disable: () => void, unregister: (hotkey, callback) => void, register: (hotkey, callback) => void}}
 * @example
 * this.utils.Hotkeys.createContext().register(
 *     'alt+t',
 *     ( event ) => {
 *         console.log( 'Hotkeys Event:', event );
 *     }
 * );
 */
export const createContext = ( options: IHotkeysOptions = { autoEnable: true, debounceTime: 500 } ): IHotkeyContext => {
	options = validateContext( options );

	const listeners: IHotkeysListener[] = [];
	const keyDownListener = createKeyDownListener(
		listeners,
		options.debounceTime,
	);

	const enable = () => document.addEventListener(
		KeyEvent.KEYDOWN,
		keyDownListener,
	);
	const disable = () => document.removeEventListener(
		KeyEvent.KEYDOWN,
		keyDownListener,
	);

	if ( options.autoEnable ) {
		enable();
	}

	return {
		register: createListenersFunction( listeners, registerListener ),
		unregister: createListenersFunction( listeners, unregisterListener ),
		enable,
		disable,
	};
};
